import React from "react";
import {
  Box, Avatar, IconButton, Menu, MenuItem,
  Typography, ListItemIcon, Divider, ButtonBase,
} from "@mui/material";
import { motion, LayoutGroup } from "framer-motion";
import LogoutIcon from "@mui/icons-material/Logout";
import axios from "axios";

const HEADER_HEIGHT = 52;
const GLASS_BG = "rgba(255,255,255,0.88)";
const GLASS_BLUR = "14px";

export default function HeaderBar({
  THEME_CONFIGS,
  selectedSection,
  setThemeInUrl,
  user,
  onLogout,
}) {
  const [menuEl, setMenuEl] = React.useState(null);
  const openMenu = (e) => setMenuEl(e.currentTarget);
  const closeMenu = () => setMenuEl(null);

  const handleLogout = () => {
    axios.post("/api/user/logout/").finally(() => {
      if (typeof onLogout === "function") onLogout();
      window.location.href = "/";
    });
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "U";

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1300,
        width: "100%",
        height: HEADER_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, md: 3 },
        backgroundColor: GLASS_BG,
        backdropFilter: `blur(${GLASS_BLUR})`,
        WebkitBackdropFilter: `blur(${GLASS_BLUR})`,
        borderBottom: "1px solid rgba(0,0,0,0.07)",
        flexShrink: 0,
      }}
    >
      {/* ── Left: branding ──────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 180 }}>
        <Box
          component="img"
          src="/images/large_logo.png"
          alt="Electorate Profiles"
          sx={{ height: 52, userSelect: "none" }}
        />
        {/* <Box
          sx={{
            px: 1, py: 0.25, borderRadius: 99,
            fontSize: "10px", fontWeight: 700,
            color: "#6b7280",
            bgcolor: "rgba(0,0,0,0.05)",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontFamily: "'DM Sans', sans-serif",
            lineHeight: 1.6,
          }}
        >
          New Zealand
        </Box> */}
      </Box>

      {/* ── Center: animated pill tabs ───────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          p: 0.5,
          borderRadius: 99,
          bgcolor: "rgba(0,0,0,0.04)",
        }}
      >
        <LayoutGroup id="nz-header-tabs">
          {Object.entries(THEME_CONFIGS || {}).map(([key, cfg]) => {
            const isSelected = selectedSection === key;
            return (
              <ButtonBase
                key={key}
                disableRipple
                onClick={() => setThemeInUrl?.(key)}
                sx={{
                  position: "relative",
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 99,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                  color: isSelected ? "#2563eb" : "#6b7280",
                  transition: "color 0.2s ease",
                  zIndex: 1,
                  "&:hover": {
                    color: isSelected ? "#2563eb" : "#1a1d23",
                    bgcolor: !isSelected ? "rgba(0,0,0,0.04)" : "transparent",
                  },
                }}
              >
                <span style={{ position: "relative", zIndex: 2 }}>{cfg.label}</span>
                {isSelected && (
                  <Box
                    component={motion.div}
                    layoutId="active-pill"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 99,
                      bgcolor: "white",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.04)",
                      zIndex: 1,
                    }}
                  />
                )}
              </ButtonBase>
            );
          })}
        </LayoutGroup>
      </Box>

      {/* ── Right: avatar + dropdown ─────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", minWidth: 180 }}>
        {user?.email ? (
          <>
            <IconButton
              onClick={openMenu}
              size="small"
              sx={{
                p: 0.5,
                transition: "all 0.2s",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                ...(Boolean(menuEl) && { bgcolor: "rgba(0,0,0,0.05)" }),
              }}
            >
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: "#2563eb",
                  fontSize: "12px",
                  fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {initials}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={menuEl}
              open={Boolean(menuEl)}
              onClose={closeMenu}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    mt: 1.5,
                    minWidth: 200,
                    borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    backdropFilter: "blur(10px)",
                    bgcolor: "rgba(255,255,255,0.95)",
                  },
                },
              }}
            >
              <Box sx={{ px: 2.5, py: 1.5 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "13px", fontFamily: "'DM Sans', sans-serif", color: "#1a1d23" }}
                >
                  {user?.name || "User"}
                </Typography>
                <Typography
                  sx={{ fontSize: "11px", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif", mt: 0.2 }}
                >
                  {user?.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={handleLogout}
                sx={{ py: 1.25, px: 2.5, borderRadius: "8px", mx: 0.5, mb: 0.5 }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: "#ef4444" }} />
                </ListItemIcon>
                <Typography
                  sx={{ fontSize: "13px", color: "#ef4444", fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}
                >
                  Log out
                </Typography>
              </MenuItem>
            </Menu>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
